import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, Box, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	triggerRefresh,
	setItemId,
	resetSearchFields,
	setSelectedRows,
} from '@/features/table/attachmentsTableSlice';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { specialCharsGlobal } from '@/utils/constants';
import { showToast } from '@/features/toast/toastSlice';
import { useAttachmentsContext } from '@/context/attachmentsContext';
import { FolderBreadcrumbs } from './FolderBreadcrumbs';
import { AttachmentsToolbar } from './AttachmentsToolbar';
import { AttachmentsTableHeader } from './AttachmentsTableHeader';
import { getAttachmentsColumns } from './data';
import { openDialog } from '@/features/dialog/dialogSlice';
import useCellModes from '@/hooks/useCellModes';
import {
	useAddMetadataToFileMutation,
	useRenameSharepointItemMutation,
} from '@/features/msGraph/msGraphApi';
import InfiniteTablePagination from '../InfiniteTablePagination';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '../../table/DataGridComponents/DataGridColumnMenu';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext.jsx';

export const AttachmentsTable = () => (
	<ColumnControlContextProvider variant='documents' value='list'>
		<AttachmentsTableContent />
	</ColumnControlContextProvider>
);

const AttachmentsTableContent = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const gridWrapRef = useRef();
	const elementRef = useRef();
	const apiRef = useGridApiRef();

	const {
		deal,
		files,
		count,
		paginationModel,
		setPaginationModel,
		sortModel,
		setSortModel,
		sharepoint,
		isLoading,
		isError,
		errorMessage,
		hasNextPage,
		hasPreviousPage,
	} = useAttachmentsContext();

	const { loading, error, refreshIndicator } = useSelector((state) => state.attachmentsTable);

	const [rowSelectionModel, setRowSelectionModel] = useState([]);

	const { cellModesModel, handleCellModesModelChange } = useCellModes();

	const anyCellsBeingEdited = Object.values(cellModesModel).some((v) => v?.name?.mode == 'edit');

	const [renameSharepointItem] = useRenameSharepointItemMutation();
	const [addMetadataToFile] = useAddMetadataToFileMutation();

	const handleReset = () => {
		dispatch(resetSearchFields());
	};

	const setActiveFolder = (row) => {
		handleReset();
		dispatch(setItemId(row.id));
	};

	const allLoading = loading || isLoading;
	const allError = error || isError;

	const handlePreview = (row) => {
		if (!row.isFolder) {
			window.open(`${row.webUrl}?web=1`, '_blank', 'noopener,noreferrer');
		}
	};

	const handleRename = (row) => {
		dispatch(
			openDialog({
				dialogName: 'renameFile',
				data: { file: row },
			}),
		);
	};

	const columns = getAttachmentsColumns({ setActiveFolder, handlePreview, handleRename });

	const {
		columns: orderedColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange: handleColumnVisibilityModelChange,
		onColumnWidthChange: handleColumnWidthChange,
	} = useDataGridColumnProps(columns);

	useEffect(() => {
		console.log('[AttachmentsTable] displayed files: ', files);
	}, [files]);

	const updateDescription = async (newRow, oldRow) => {
		const metadata = {
			_ExtendedDescription: newRow.description.trim(),
		};
		await addMetadataToFile({
			itemId: oldRow.id,
			driveId: oldRow.parentReference.driveId,
			metadata,
		}).unwrap();
	};

	const updateFileName = async (newRow, oldRow) => {
		const cleanedName = newRow.name.trim().replace(specialCharsGlobal, '').slice(0, 250);
		const type =
			!oldRow.isFolder && !cleanedName.endsWith(`.${oldRow.type}`) ? `.${oldRow.type}` : '';

		const newName =
			cleanedName.replace(type, '').length === 0 ? oldRow.name : `${cleanedName}${type}`;
		if (newName != oldRow.name) {
			await renameSharepointItem({
				driveId: oldRow.parentReference.driveId,
				itemId: oldRow.id,
				name: newName,
			}).unwrap();
			return true;
		} else {
			return false;
		}
	};

	const processRowUpdate = async (newRow, oldRow) => {
		if (oldRow.description !== newRow.description) {
			try {
				await updateDescription(newRow, oldRow);
				dispatch(
					showToast({
						message: 'Description updated!',
						success: true,
					}),
				);
			} catch (error) {
				const errorMessage = error?.response?.data?.error?.message?.value ?? '';
				console.log('🙅 ~ ERROR UPDATING METADATA', error);
				dispatch(
					showToast({
						message: `Failed to update description: ${errorMessage}`,
						error: true,
					}),
				);
			}
		}
		if (oldRow.name !== newRow.name) {
			console.log('NEW ROW', newRow);
			try {
				const renamed = await updateFileName(newRow, oldRow);
				if (renamed) {
					dispatch(
						showToast({
							message: `${oldRow.isFolder ? 'Folder' : 'File'} name updated!`,
							success: true,
						}),
					);
				}
			} catch (error) {
				console.log('error?.response?.data', error?.response?.data);
				const errorMessageString = error?.response?.data?.error?.message;
				const errorMessage =
					typeof errorMessageString === 'string'
						? errorMessageString
						: (error?.response?.data?.error?.message?.value ?? '');
				console.log('🙅 ~ ERROR UPDATING METADATA', error);
				dispatch(
					showToast({
						message: `Failed to update ${
							oldRow.isFolder ? 'Folder' : 'File'
						} name: ${errorMessage}`,
						error: true,
					}),
				);
				dispatch(triggerRefresh());
			}
		}
		return newRow;
	};

	const handleProcessRowUpdateError = (error) => {
		console.log('Error updating row', error);
	};

	useEffect(() => handleReset(), []);

	useEffect(() => {
		if (rowSelectionModel.length > 0) {
			setRowSelectionModel([]);
		}
	}, [refreshIndicator]);

	useEffect(() => {
		const items = rowSelectionModel.map((id) => files.find((r) => r.id == id));
		dispatch(setSelectedRows(items.filter((i) => i)));
	}, [rowSelectionModel]);

	const TablePagination = useCallback(
		() => (
			<InfiniteTablePagination
				{...{
					count,
					page: paginationModel.page,
					setPage: (page) => setPaginationModel({ page }),
					rowsPerPage: 50,
					hasNextPage,
					hasPreviousPage,
				}}
			/>
		),
		[count, paginationModel.page, hasNextPage, hasPreviousPage],
	);

	return (
		<Box
			sx={{ width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}
			ref={gridWrapRef}
		>
			<AttachmentsTableHeader
				loading={allLoading}
				selectionCount={rowSelectionModel.length}
				anyCellsBeingEdited={anyCellsBeingEdited}
			/>
			<FolderBreadcrumbs />
			{!allLoading && !sharepoint?.isValid ? (
				<Typography variant='subtitle'>
					{`Could not find valid Sharepoint site for ${
						deal ? 'deal' : 'client'
					} owner. Please contact support@folio.insure.`}
				</Typography>
			) : (
				<Box
					sx={{
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: '8px',
						backgroundColor: theme.palette.background.paper,
						'& .MuiDataGrid-root': {
							borderRadius: '8px',
						},
					}}
				>
					<DataGrid
						ref={elementRef}
						apiRef={apiRef}
						loading={allLoading}
						autoHeight
						width='100%'
						getRowHeight={() => 'auto'}
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
						onColumnWidthChange={handleColumnWidthChange}
						initialState={{
							sorting: {
								sortModel: [{ field: 'lastModifiedDateTime', sort: 'desc' }],
							},
						}}
						sortingOrder={['desc', 'asc']}
						sx={{
							border: 'none',
							'& .MuiDataGrid-editInputCell': {
								backgroundColor: 'transparent',
							},
							'& .MuiDataGrid-cell[data-field="name"]': {
								paddingX: '0',
							},
							'--DataGrid-overlayHeight': allLoading || allError ? '5em' : '3em',
						}}
						isCellEditable={(params) => {
							if (params.row.isFolder) {
								if (params.field == 'name' && !params.row.cannotDelete) {
									return true;
								}
								return false;
							}
							if (params.row.cannotDelete && params.field == 'name') {
								return false;
							}
							return params.colDef?.editable;
						}}
						getRowClassName={(params) => {
							const { row } = params;
							return row.isFolder ? 'file-upload-row' : '';
						}}
						processRowUpdate={processRowUpdate}
						cellModesModel={cellModesModel}
						onCellModesModelChange={handleCellModesModelChange}
						onProcessRowUpdateError={handleProcessRowUpdateError}
						rows={files}
						columns={orderedColumns}
						isRowSelectable={(params) => !params.row.cannotDelete}
						onRowSelectionModelChange={(newRowSelectionModel) => {
							setRowSelectionModel(newRowSelectionModel);
						}}
						rowSelectionModel={rowSelectionModel}
						checkboxSelection={true}
						disableRowSelectionOnClick
						disableSelectionOnClick
						disableDensitySelector
						slots={{
							noRowsOverlay: allError
								? () => (
										<NoRowsErrorOverlay
											objectType={'files and folders'}
											message={errorMessage}
										/>
									)
								: () => <NoRowsOverlay objectType={'files or folders'} />,
							toolbar: AttachmentsToolbar,
							pagination: TablePagination,
							columnMenu: DataGridColumnMenu,
						}}
						slotProps={{
							loadingOverlay: {
								variant: 'skeleton',
								noRowsVariant: 'skeleton',
							},
						}}
						sortModel={sortModel}
						onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
						paginationMode='server'
						sortingMode='server'
						filterMode='server'
						rowCount={count}
						paginationModel={paginationModel}
						pageSizeOptions={[10]}
					/>
				</Box>
			)}
		</Box>
	);
};
